import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Settings2, Search } from 'lucide-react'
import { useAnalytics } from '@/hooks/useAnalytics'

interface ClipViewConfig {
  visibleMetadataColumns: string[]
}

interface ColumnPickerProps {
  projectId: string
  availableFields: string[]
  selectedFields: string[]
  onSelectionChange: (fields: string[]) => void
}

// Display name mappings for metadata fields (maps field name to display name)
export const METADATA_DISPLAY_NAMES: Record<string, string> = {
  // Core fields
  'Name': 'Name',
  'Tape': 'Tape Name',
  'Start': 'Start',
  'Duration': 'Duration',
  // Custom metadata fields
  'Descript': 'Description',
  'Scene': 'Scene',
  'Slate': 'Slate',
  'Take': 'Take',
  'End': 'END',
  'Sound TC': 'Sound TC',
  'Camera TC': 'Camera TC',
  'TC 24': 'TC 24',
  'Soundroll': 'Sound Roll',
  'Camroll': 'Camera Roll',
  'Labroll': 'Lab Roll',
  'Shoot Date': 'Shoot Date',
  'Shoot Day': 'Shoot Day',
  'Episode': 'Episode',
  'Comments': 'Comments',
  'UID': 'UID',
  'Tracks': 'Tracks',
  'Auxiliary TC1': 'Aux TC1',
  'Filename': 'Filename',
  'Filepath': 'File Path',
  'StartFrameNumber': 'Start Frame',
  'EndFrameNumber': 'End Frame',
  'Length': 'Length',
  'ASC_SOP': 'ASC SOP',
  'ASC_SAT': 'ASC SAT',
  'ColorSpace': 'Color Space',
  'Kelvin': 'Kelvin',
  'ISO': 'ISO',
  'LUTName': 'LUT Name',
  'LUT1': 'LUT 1',
  'LUT2': 'LUT 2',
  'Audiofile': 'Audio File',
  'Audiotrack1': 'Audio Track 1',
  'Audiotrack2': 'Audio Track 2',
  'Audiotrack3': 'Audio Track 3',
  'Audiotrack4': 'Audio Track 4',
  'Audiotrack5': 'Audio Track 5',
  'Audiotrack6': 'Audio Track 6',
  'Audiotrack7': 'Audio Track 7',
  'Audiotrack8': 'Audio Track 8',
  'lens_focal_length': 'Focal Length',
  'lens_aperture': 'Aperture',
  'lens_focus_distance': 'Focus Distance',
  'shutter_degrees': 'Shutter',
  'white_balance_kelvin': 'White Balance',
}

// Helper function to get display name for a field
export function getMetadataDisplayName(fieldName: string): string {
  return METADATA_DISPLAY_NAMES[fieldName] || fieldName
}

export function ColumnPicker({
  projectId,
  availableFields,
  selectedFields,
  onSelectionChange,
}: ColumnPickerProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { track } = useAnalytics()

  // Filter fields based on search query
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return availableFields
    const query = searchQuery.toLowerCase()
    return availableFields.filter(field => {
      const displayName = getMetadataDisplayName(field).toLowerCase()
      const fieldName = field.toLowerCase()
      return displayName.includes(query) || fieldName.includes(query)
    })
  }, [availableFields, searchQuery])

  const handleToggleField = (field: string) => {
    const newSelection = selectedFields.includes(field)
      ? selectedFields.filter(f => f !== field)
      : [...selectedFields, field]

    onSelectionChange(newSelection)

    // Track column toggle
    track('clips_columns_changed', {
      project_id: projectId,
      action: selectedFields.includes(field) ? 'removed' : 'added',
      column: field,
      total_columns: newSelection.length,
    })

    // Save to localStorage
    const config: ClipViewConfig = {
      visibleMetadataColumns: newSelection,
    }
    localStorage.setItem(`clipView_v2_${projectId}`, JSON.stringify(config))
  }

  const handleSelectAll = () => {
    onSelectionChange(availableFields)

    // Track select all
    track('clips_columns_changed', {
      project_id: projectId,
      action: 'select_all',
      total_columns: availableFields.length,
    })

    const config: ClipViewConfig = {
      visibleMetadataColumns: availableFields,
    }
    localStorage.setItem(`clipView_v2_${projectId}`, JSON.stringify(config))
  }

  const handleClearAll = () => {
    onSelectionChange([])

    // Track clear all
    track('clips_columns_changed', {
      project_id: projectId,
      action: 'clear_all',
      total_columns: 0,
    })

    const config: ClipViewConfig = {
      visibleMetadataColumns: [],
    }
    localStorage.setItem(`clipView_v2_${projectId}`, JSON.stringify(config))
  }


  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen)
      if (!isOpen) setSearchQuery('')
    }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4 mr-2" />
          Columns
          {selectedFields.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">
              ({selectedFields.length})
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Custom Metadata Columns</h4>
            <p className="text-xs text-muted-foreground">
              Select which metadata fields to display as columns
            </p>
          </div>

          {availableFields.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No metadata fields available
            </p>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search columns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="flex-1 text-xs"
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="flex-1 text-xs"
                >
                  Clear All
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-3">
                {filteredFields.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    No matching columns
                  </p>
                ) : filteredFields.map((field) => (
                  <div key={field} className="flex items-center space-x-2">
                    <Checkbox
                      id={`field-${field}`}
                      checked={selectedFields.includes(field)}
                      onCheckedChange={() => handleToggleField(field)}
                    />
                    <label
                      htmlFor={`field-${field}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {getMetadataDisplayName(field)}
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
